import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocale } from "@ncp/i18n";
import { useAssistantStream, useCaseThread, type KnowledgeSourceKey } from "@ncp/assistant-sdk";
import { hidePartial, stripWidgetMarkers } from "@ncp/ui/lib/inlineWidgets";
import { saveFailureText } from "@ncp/ui/lib/save";
import type { AssistantCitation, AssistantRequest, AssistantUpdate, Mission } from "../mission/engine";

/** The platform agent that answers free text (the case assistant). Its runs are the lead's or a box's voice, not a card on the canvas. */
export const ASSISTANT_AGENT_KEY = "competition-assistant";

interface Ask {
  /** Sends the officer's question to the lead ("lead") or to one agent (its id); questions wait their turn behind agents' research. */
  ask: (target: string, text: string) => void;
  /** The box whose question is being answered (or waits), while it streams. */
  answering: string | null;
}
const AskContext = createContext<Ask | null>(null);

export function useAsk(): Ask {
  const ctx = useContext(AskContext);
  if (!ctx) throw new Error("useAsk must be used inside <AskProvider>");
  return ctx;
}

/** What one agent has established, so the answer to a question put to it starts from its own work. */
function framed(mission: Mission, agentId: string, text: string): string {
  const s = mission.getSnapshot();
  const agent = s.agents[agentId];
  if (!agent) return text;
  const findings = agent.findings.map((f) => `- ${f.text.en}`).join("\n");
  return `${text}\n\n(Asked of the ${agent.name.en} – ${agent.role.en} – in the agentic review of ${s.caseNumber}.${findings ? ` What it has established so far:\n${findings}\n` : " "}Answer as that agent, briefly, from the file and the platform's tools, in the language of the question.)`;
}

interface Job { prompt: string; sources?: KnowledgeSourceKey[]; target: string | null; onUpdate: (u: AssistantUpdate) => void; resolve: (u: AssistantUpdate) => void; tries?: number }

/**
 * One conversation with the platform's case assistant for the whole review (the thread the case's Assistant button opens), serving both
 * the officer's questions (to the lead or to one agent) and the agents' own research (the open web): one turn at a time, in order, each
 * streamed into the box that asked.
 */
export function AskProvider({ mission, children }: { mission: Mission; children: ReactNode }) {
  const { locale } = useLocale();
  const caseId = mission.getSnapshot().caseId;
  const thread = useCaseThread("backoffice");
  const threadId = thread.data?.id;
  const sources = useRef<KnowledgeSourceKey[] | undefined>(undefined);
  const stream = useAssistantStream(threadId, { sources: () => sources.current ?? null });
  const queue = useRef<Job[]>([]);
  const current = useRef<Job | null>(null);
  const sentAt = useRef(0);
  const started = useRef(false);
  // Set once the turn's final frame (or its error) has been written into the box; the job closes then, not before.
  const settled = useRef(false);
  const closing = useRef<(() => void) | null>(null);
  const last = useRef<AssistantUpdate>({ text: "", tools: [], citations: [], streaming: false });
  const [answering, setAnswering] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  // The next job goes out when the conversation exists and nothing else is streaming; it closes when its turn has been saved.
  useEffect(() => {
    if (current.current || stream.streaming || !queue.current.length) return;
    if (!threadId) {
      if (!thread.isPending && !thread.isError) thread.mutate({ caseId, locale });
      return;
    }
    const job = queue.current.shift()!;
    current.current = job;
    sources.current = job.sources;
    sentAt.current = Date.now();
    started.current = false;
    settled.current = false;
    last.current = { text: "", tools: [], citations: [], streaming: true };
    setAnswering(job.target);
    const close = () => {
      if (current.current !== job) return;
      current.current = null;
      closing.current = null;
      if (!started.current && (job.tries ?? 0) < 3) {
        // The stream was not free after all: the job goes back to the front of the queue.
        job.tries = (job.tries ?? 0) + 1;
        queue.current.unshift(job);
      } else {
        const done: AssistantUpdate = { ...last.current, streaming: false, error: last.current.error ?? (started.current ? undefined : "The case assistant did not answer.") };
        job.onUpdate(done);
        job.resolve(done);
      }
      setAnswering(queue.current[0]?.target ?? null);
      window.setTimeout(() => setTick((t) => t + 1), 250);
    };
    void stream.send(job.prompt, { locale }).catch(() => undefined).finally(() => {
      // The hook's last frames may not have reached the box yet: wait for them (briefly) before closing.
      if (!started.current || settled.current) close();
      else { closing.current = close; window.setTimeout(close, 2000); }
    });
  }, [tick, threadId, stream, locale, thread, caseId]);

  // A conversation that could not be opened fails every waiting job instead of leaving the boxes spinning.
  useEffect(() => {
    if (!thread.isError) return;
    const failed = queue.current.splice(0);
    failed.forEach((j) => { const u: AssistantUpdate = { text: "", tools: [], citations: [], streaming: false, error: saveFailureText(thread.error) }; j.onUpdate(u); j.resolve(u); });
    setAnswering(null);
  }, [thread.isError, thread.error]);

  // The live answer, mirrored into its box while it streams (never an earlier turn's, which the hook keeps until the saved one arrives).
  const live = stream.live;
  useEffect(() => {
    const job = current.current;
    if (!live || !job || live.startedAt < sentAt.current) return;
    started.current = true;
    const citations: AssistantCitation[] = live.citations.map((c) => ({ title: c.title, url: c.url ?? null, publisher: c.publisher ?? null, sourceType: c.sourceType, excerpt: c.excerpt }));
    // The answer's widget markers place cards the case assistant's own pane draws; a box shows the prose only.
    const text = stripWidgetMarkers(live.final?.content || live.content);
    last.current = { text: stream.streaming ? hidePartial(text) : text, tools: live.toolCalls.map((c) => c.tool), citations, streaming: stream.streaming, error: live.error ?? undefined };
    job.onUpdate(last.current);
    if (!stream.streaming && (live.final || live.error)) {
      settled.current = true;
      closing.current?.();
    }
  }, [live, stream.streaming]);

  const enqueue = useCallback((job: Job) => {
    queue.current.push(job);
    if (!current.current) setAnswering(job.target);
    setTick((t) => t + 1);
  }, []);

  // The agents' research goes through the same conversation.
  useEffect(() => {
    mission.setAssistant((request: AssistantRequest) => new Promise<AssistantUpdate>((resolve) => {
      enqueue({ prompt: request.prompt, sources: request.sources as KnowledgeSourceKey[] | undefined, target: null, onUpdate: (u) => request.onUpdate?.(u), resolve });
    }));
    return () => mission.setAssistant(null);
  }, [mission, enqueue]);

  const ask = useCallback((target: string, text: string) => {
    const prompt = target === "lead" ? text : framed(mission, target, text);
    let write: (u: AssistantUpdate) => void;
    if (target === "lead") {
      mission.userSaid(text);
      const replyId = mission.chat({ text: "", streaming: true });
      write = (u) => mission.chat({ id: replyId, text: u.text, streaming: u.streaming, tools: u.tools, error: u.error });
    } else {
      mission.officerAsks(target, text);
      const replyId = `reply-${Date.now()}`;
      mission.agentAnswers(target, { id: replyId, text: "", streaming: true });
      write = (u) => mission.agentAnswers(target, { id: replyId, text: u.text, streaming: u.streaming, tools: u.tools, error: u.error });
    }
    enqueue({ prompt, target, onUpdate: write, resolve: () => undefined });
  }, [mission, enqueue]);

  const value = useMemo(() => ({ ask, answering }), [ask, answering]);
  return <AskContext.Provider value={value}>{children}</AskContext.Provider>;
}
